import { useCallback, useEffect, useRef, useState } from 'react';
import { Crown, Eye, EyeOff, RefreshCw, ShieldAlert, Trophy, X } from 'lucide-react';

const money = (value) => `$${Number(value || 0).toLocaleString()}`;

function ModeBadge({ entry }) {
  return <span className={`legend-badge mode-${entry.modeId}`}>{entry.mode}</span>;
}

function DifficultyBadge({ value }) {
  return <span className={`legend-badge difficulty-${value}`}>{String(value || '').toUpperCase()}</span>;
}

function RankMark({ rank }) {
  if (rank === 1) return <span className="legend-rank medal gold"><Crown aria-hidden="true" />1</span>;
  if (rank === 2) return <span className="legend-rank medal silver">2</span>;
  if (rank === 3) return <span className="legend-rank medal bronze">3</span>;
  return <span className="legend-rank">#{rank}</span>;
}

function LegendCard({ entry, mine, section }) {
  return (
    <article className={`legend-card ${mine ? 'current-player' : ''}`}>
      <div className="legend-card-top">
        <RankMark rank={entry.rank} />
        <div className="legend-identity"><strong>{entry.alias}</strong><small>{entry.modeId === 'career' ? `Day ${entry.day} · ${entry.residence || 'Dumpster'}` : `Day ${entry.day}/${entry.maxDays}`}</small></div>
        {mine && <span className="legend-you">YOU</span>}
      </div>
      <div className="legend-badges"><ModeBadge entry={entry} /><DifficultyBadge value={entry.difficulty} />{entry.debtClearedDay != null && <span className="legend-badge debt-cleared">DEBT CLEARED · DAY {entry.debtClearedDay}</span>}{entry.modeId === 'career' && entry.doubleWideDay != null && <span className="legend-badge career-home">DOUBLE-WIDE · DAY {entry.doubleWideDay}</span>}</div>
      <dl className="legend-stats">
        <div><dt>{section === 'hall' ? 'Street score' : 'Net worth'}</dt><dd>{money(section === 'hall' ? entry.streetScore : entry.netWorth)}</dd></div>
        <div><dt>Cash</dt><dd>{money(entry.cash)}</dd></div>
        <div><dt>Bank</dt><dd>{money(entry.bank)}</dd></div>
        <div><dt>Debt</dt><dd className={entry.debt > 0 ? 'danger-text' : 'success-text'}>{money(entry.debt)}</dd></div>
        <div><dt>Rep</dt><dd>{entry.streetReputation}</dd></div>
        <div><dt>Health</dt><dd>{entry.health}</dd></div>
        {entry.modeId === 'career' && <div><dt>Best net worth</dt><dd>{money(entry.highestNetWorth ?? entry.netWorth)}</dd></div>}
        {entry.modeId === 'career' && <div><dt>Residence</dt><dd>{entry.residence || 'Dumpster'}</dd></div>}
      </dl>
      {section === 'hall' && <div className={`legend-outcome outcome-${entry.outcome}`}>{String(entry.outcome || 'completed').replace('_', ' ')}</div>}
    </article>
  );
}

function EmptyLegends({ section }) {
  return <div className="legend-empty"><Trophy aria-hidden="true" /><strong>No ranked {section === 'hall' ? 'finished runs' : 'active hustlers'} yet.</strong><span>Somebody has to be the first bad influence.</span></div>;
}

export function LeaderboardModal({ open, onClose, actions }) {
  const [section, setSection] = useState('active');
  const [modeId, setModeId] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const actionsRef = useRef(actions);
  useEffect(() => { actionsRef.current = actions; }, [actions]);

  const load = useCallback(async () => {
    if (!open) return;
    setBusy(true);
    setError('');
    try {
      const response = await actionsRef.current.loadLeaderboard({ modeId, difficulty, limit: 50 });
      setBoard(response?.leaderboard || null);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }, [open, modeId, difficulty]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const keydown = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  }, [open, onClose]);

  if (!open) return null;
  const entries = section === 'hall' ? (board?.hallOfFame || []) : (board?.active || []);
  const privacyVisible = board?.privacy?.visible !== false;
  const mine = board?.me || null;

  const togglePrivacy = async () => {
    setBusy(true);
    setError('');
    try {
      await actionsRef.current.setLeaderboardVisibility(!privacyVisible);
      await load();
    } catch (requestError) {
      setError(requestError.message);
      setBusy(false);
    }
  };

  return (
    <div className="leaderboard-overlay" role="dialog" aria-modal="true" aria-labelledby="street-legends-title">
      <section className="leaderboard-modal glass-panel">
        <header className="leaderboard-header">
          <div><span className="eyebrow">AUTHENTICATED PLAYERS ONLY</span><h2 id="street-legends-title"><Trophy aria-hidden="true" /> Street Legends</h2><p>Current hustlers and finished runs ranked by server-calculated values. Emails, locations, and inventory stay private.</p></div>
          <button className="leaderboard-close" onClick={onClose} aria-label="Close leaderboard"><X /></button>
        </header>

        <div className="leaderboard-toolbar">
          <div className="leaderboard-tabs" role="tablist" aria-label="Leaderboard section">
            <button className={section === 'active' ? 'active' : ''} onClick={() => setSection('active')}>Active Hustlers</button>
            <button className={section === 'hall' ? 'active' : ''} onClick={() => setSection('hall')}>Hall of Fame</button>
          </div>
          <div className="leaderboard-filters">
            <label>Mode<select value={modeId} onChange={(event) => setModeId(event.target.value)}><option value="">All modes</option><option value="classic_30">Classic</option><option value="long_haul_120">Long Haul</option><option value="career">Career</option></select></label>
            <label>Difficulty<select value={difficulty} onChange={(event) => setDifficulty(event.target.value)}><option value="">All</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></label>
            <button className="legend-refresh" onClick={load} disabled={busy}><RefreshCw className={busy ? 'spinning' : ''} />Refresh</button>
          </div>
        </div>

        <div className="leaderboard-privacy">
          <div>{privacyVisible ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />}<span><strong>{privacyVisible ? 'Your eligible runs are visible' : 'Your runs are private'}</strong><small>You can still view Street Legends either way.</small></span></div>
          <button onClick={togglePrivacy} disabled={busy}>{privacyVisible ? 'Hide my runs' : 'Show my runs'}</button>
        </div>

        {error && <div className="leaderboard-error"><ShieldAlert />{error}</div>}
        {mine && (!mine.visible || !mine.eligible) && (
          <div className="legend-self-status">
            <strong>Your current run is {mine.visible ? 'unranked' : 'private'}.</strong>
            <span>{!mine.visible ? 'Enable visibility to publish eligible run stats.' : (mine.eligibilityReason || 'This run is not eligible for official ranking.')}</span>
          </div>
        )}

        <div className="leaderboard-list" aria-live="polite">
          {busy && !board ? <div className="legend-loading"><RefreshCw className="spinning" />Loading Street Legends…</div> : entries.length ? entries.map((entry) => <LegendCard key={entry.entryId} entry={entry} mine={Boolean(entry.isCurrentPlayer)} section={section} />) : <EmptyLegends section={section} />}
        </div>

        <footer className="leaderboard-footer">
          <span>Active board ranks net worth. Hall of Fame ranks Street Score.</span>
          <span>Admin-adjusted and pre-leaderboard runs remain unranked.</span>
        </footer>
      </section>
    </div>
  );
}
